import React from "react";

import { DragSource, DropTarget, DropTargetMonitor } from "react-dnd";

import { ElementType, DynamoNode, NodeTag, ElementNode } from "dynamo-models";
import { INodePropsInvar, TYPE } from "..";
import { doArrangeNode, doSetSelectedNode } from "../..";
import { INodeDropCollectedProps, INodeDragCollectedProps, NodeProps } from "../props";
import { connect } from "react-redux";
import { stateToProps } from "../primary";
import { Card, CardHeader, CardContent, Paper, Typography } from "@material-ui/core";
import { IEditorState } from "../../redux";

export const FAIR_COLOR = "#cce6ff";
export const SELECT_COLOR = "#c5f0b4";

const getBackground = (props: NodeProps<DynamoNode>) => {
    if (props.isOverShallow) {
        return FAIR_COLOR;
    }

    if (props.selectedNodeId === props.node.nodeId) {
        return SELECT_COLOR;
    }

    if (props.isOverDeep) {
        return "#ffe6e6";
    }
};

const getOpacity = (props: INodePropsInvar) => props.isDragging ? 0 : 1;

const getDragStyle = (props: INodePropsInvar): React.CSSProperties => ({
    opacity: getOpacity(props),
    borderRadius: 10,
    borderWidth: 1,
    alignSelf: "stretch",
    width: "100%",
    gridArea: `1 / ${props.keyIndex + 1} / span 1 / span 1`,
});

const getDropStyle = (nodeProps: NodeProps<DynamoNode>) => (props: INodeDropCollectedProps): React.CSSProperties => ({
    padding: nodeProps.node.tag !== NodeTag.Page ? nodeProps.spacing : 10,

    backgroundColor: getBackground(nodeProps),

    borderWidth: 0,

    borderRadius: 10,

    borderColor: FAIR_COLOR,

    borderStyle: "solid",

    margin: 10,
});

const TargetSpecs = {
    drop(props: IDropProps, monitor: DropTargetMonitor, component) {
        if (!component || monitor.getDropResult() !== null) {
            return;
        }

        const sourceCoord = monitor.getClientOffset();
        const targetRef: HTMLDivElement = component.innerRef.current;
        const targetDoms = targetRef.children[0].children;

        if (sourceCoord === null) {
            return;
        }

        let minOffsetX = Number.MAX_VALUE;
        let minOffsetY = Number.MAX_VALUE;
        let prospIndex = -1;
        let currIndex = 0;

        for (const currChild of targetDoms) {
            const rect = currChild.getBoundingClientRect();
            const offsetX = rect.left - sourceCoord.x;
            const offsetY = rect.bottom - sourceCoord.y;

            if ((offsetX > 0 && offsetX < minOffsetX) ||
                props.orientation === Orientation.vertical &&
                offsetY > 0 && offsetY < minOffsetY) {
                minOffsetX = offsetX;
                minOffsetY = offsetY;
                prospIndex = currIndex;
            }

            currIndex++;
        }

        prospIndex = prospIndex === -1 ? currIndex : prospIndex;

        const resultMaybe = props.onDrop(monitor.getItem().node, prospIndex);
        if (resultMaybe !== undefined) {
            return resultMaybe;
        }

        return;
    },

    canDrop(props, monitor) {
        return true;
    },
};

const SourceSpecs = {
    beginDrag(props, monitor, component) {
        return { ...props };
    },
};

export enum DragDropType {
    primary = "primary",
    toolbox = "toolbox"
}

export enum Orientation {
    horizontal = "horizontal",
    vertical = "vertical"
}

export interface IDropProps {
    getStyle: (collectedProps: INodeDropCollectedProps) => React.CSSProperties;

    orientation: Orientation;

    onDrop: (droppable: any, index: number) => any | undefined;

    getRender: (props: INodeDropCollectedProps) => JSX.Element;
}

export interface IDragProps extends INodeDragCollectedProps {
    id: string;
}

class DropBox extends React.Component<IDropProps & INodeDropCollectedProps> {
    public innerRef: React.RefObject<HTMLDivElement> = React.createRef();

    public render() {
        return this.props.connectDropTarget(
            <div style={this.props.getStyle(this.props)} ref={this.innerRef}>
                {this.props.getRender(this.props)}
            </div>
        );
    }
}

class DragBox extends React.Component<INodePropsInvar> {
    public innerRef: React.RefObject<HTMLDivElement> = React.createRef();

    public render() {
        return this.props.connectDragSource(
            <div style={getDragStyle(this.props)} ref={this.innerRef}>
                {this.props.children}
            </div>,
        );
    }
}

export class DragDropContainer extends React.Component<INodePropsInvar> {
    public render() {
        return withDragDrop;
    }
}

export const DropContainer = DropTarget(TYPE, TargetSpecs, (connect, monitor) => ({
    connectDropTarget: connect.dropTarget(),
    isOverDeep: monitor.isOver(),
    isOverShallow: monitor.isOver({ shallow: true }),
    canDrop: monitor.canDrop(),
}))(DropBox);

export const DragContainer = DragSource(TYPE, SourceSpecs, (connect, monitor) => ({
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
}))(DragBox);

export const withDropGeneral = (props) => {
    return (
        <DropContainer {...props} />
    );
} 

export const withDrag = (props: INodePropsInvar) => (children) => {
    return (
        <DragContainer {...props}>
            {children}
        </DragContainer>
    );
};

const getDispatchToProps = (dispatch) => ({
    getOnDrop: (props) => (droppable: any, index: number) => {
        const droppedNode = droppable as DynamoNode;
        const droppedNodeParent = props.MetaNode.tryFindParentOf(droppedNode.nodeId);

        if (droppedNodeParent === undefined) {
            return;
        }

        dispatch(doArrangeNode(droppedNode, props.node, index));
    },

    onSelect: (props: NodeProps<DynamoNode>) => event => {
        dispatch(doSetSelectedNode(props.node))
        event.stopPropagation()
    }
});

const getMergedProps = (_, dispatchProps, props) => ({
    onDrop: dispatchProps.getOnDrop(props),
    ...props
})

const DropComp = connect(undefined, getDispatchToProps, getMergedProps)(withDropGeneral);

export const withDrop = (props: NodeProps<DynamoNode>) => (children) => {
    const dropProps = ({
        getStyle: getDropStyle(props),
    
        orientation: props.node.tag === NodeTag.Element && (props.node as ElementNode).elementType === ElementType.Table ? Orientation.horizontal : Orientation.vertical,
    
        getRender: (props) => children,

        ...props
    });

    return (
        <DropComp { ...dropProps } />
    )
};

export const withDragDrop = (props: NodeProps<DynamoNode>) => (children) => {
    return (withDrag(props))
            ((withDrop(props))
                (children));
};

const getSelectStyle = props => ({
    borderWidth: 5,
    borderColor: "blue",
    width: "100%",
    minheight: "2em",
    gridArea: `1 / ${props.keyIndex + 1} / span 1 / span 1`,
});

const getDynamicOrDefault = (node: ElementNode, fallback: string) => node.dynamicName !== undefined ? node.dynamicName : fallback;

const getSelectHeader = node => {
    const getDynamicOrDefaultInner = fallback => getDynamicOrDefault(node, fallback);

    switch (node.elementType) {
        case ElementType.Cell:
        return getDynamicOrDefaultInner("Cell");

        case ElementType.Checkbox:
        return getDynamicOrDefaultInner("Checkbox");

        case ElementType.Date:
        return getDynamicOrDefaultInner("Date");

        case ElementType.DropdownElement:
        return getDynamicOrDefaultInner("");

        case ElementType.DropdownGroup:
        return getDynamicOrDefaultInner("Dropdown");

        case ElementType.RadioButtonElement:
        return getDynamicOrDefaultInner("Radio");

        case ElementType.RadioButtonGroup:
        return getDynamicOrDefaultInner("Radio Group");

        case ElementType.Row:
        return getDynamicOrDefaultInner("Row");

        case ElementType.Section:
        return getDynamicOrDefaultInner("Section");

        case ElementType.Table:
        return getDynamicOrDefaultInner("Table");

        case ElementType.Textbox:
        return getDynamicOrDefaultInner("Textbox");

        case ElementType.VariableText:
        return getDynamicOrDefaultInner("Text");

        case ElementType.Image:
        return getDynamicOrDefaultInner("Image");

        default:
        return "Element";
    }
}

class InnerSelect extends React.Component<any> {
    public render() {
        return (
            <Paper onClick={this.props.onSelect(this.props)} style={getSelectStyle(this.props)}>
                <Typography style={{fontSize: "1em", color: "#969696", padding: "0.5em" }}>
                    {getSelectHeader(this.props.node)}
                </Typography>
                {this.props.children}
            </Paper>
        );
    }
}

const ConnectSelect = connect(stateToProps, getDispatchToProps)(InnerSelect);

export const withSelect = props => children => (
    <ConnectSelect {...props}>
        {children}
    </ConnectSelect>
)

export const withDropSelect = (props: NodeProps<DynamoNode>) => (children) => {
    return (withDrop(props))
            ((withSelect(props))
                (children));
}

export const withDragSelect = (props: NodeProps<DynamoNode>) => (children) => {
    return (withDrag(props))
            ((withSelect(props))
                (children));
}

export const withDragDropSelect = (props: NodeProps<DynamoNode>) => (children) => {
    return (withDragDrop(props))
            ((withSelect(props))
                (children));
}
